import styled, { css } from 'styled-components';
import { backgroundTokens, colors, s16, s24 } from '@ardoq/design-tokens';
import { ISLAND_SELECTOR } from '../Island';
import { ReactNode, CSSProperties, AriaAttributes } from 'react';
import { NOTIFICATION_SELECTOR } from '@ardoq/status-ui';
import { BackgroundColor, FlexBox } from '@ardoq/layout';

type BackgroundPattern = 'dotted';

type PageBodyWrapperProps = {
  $preventScroll?: boolean;
  // Its temporal flag for backward compability with the current app layout
  // In general we are aming to have all islands centered in the app
  $alignIslandsToLeft?: boolean;
  $backgroundColor?: BackgroundColor;
  $backgroundPattern?: BackgroundPattern;
  /**
   * If true, padding will not be applied to the body.
   * You may need it if you want to embed a page body inside another page body.
   */
  $skipPadding?: boolean;
  $padding?: CSSProperties['padding'];
  $minHeight?: CSSProperties['minHeight'];
};

/**
 * @deprecated use PageBody instead
 */
export const PageBodyWrapper = styled.div<PageBodyWrapperProps>`
  height: 100%;
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor ? colors[$backgroundColor] : colors.surfaceDefaultSubtle};
  background-image: ${({ $backgroundPattern }) =>
    $backgroundPattern === 'dotted' && backgroundTokens.dotted};
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${s16};

  ${({ $alignIslandsToLeft }) =>
    !$alignIslandsToLeft &&
    css`
      ${ISLAND_SELECTOR}, ${NOTIFICATION_SELECTOR} {
        margin: 0 auto;
        width: 100%;
      }
    `}

  ${({ $skipPadding, $padding = s24 }) =>
    !$skipPadding &&
    css`
      padding: ${$padding};
    `}

  ${({ $minHeight }) =>
    $minHeight !== undefined &&
    css`
      min-height: ${$minHeight};
    `}

  ${({ $preventScroll }) =>
    $preventScroll
      ? css`
          overflow-y: hidden;

          & > ${ISLAND_SELECTOR} {
            max-height: 100%;
          }
        `
      : css`
          overflow-y: auto;
        `}
`;

export type PageBodyProps = {
  children: ReactNode;
  leftContent?: ReactNode;
  rightContent?: ReactNode;
  headerContent?: ReactNode;
  footerContent?: ReactNode;
  preventScroll?: boolean;
  alignIslandsToLeft?: boolean;
  backgroundColor?: BackgroundColor;
  backgroundPattern?: BackgroundPattern;
  skipPadding?: boolean;
  padding?: CSSProperties['padding'];
  /**
   * Minimum height of the content area. When the page is shorter, the column
   * holding `headerContent` scrolls instead of shrinking the content below this.
   */
  minContentHeight?: CSSProperties['minHeight'];
  /**
   * If true, the PageBodyWrapper will be rendered as a <main> element instead of a <div>.
   * It is important for accessibility, as it allows assistive technologies to identify the main content of the page.
   * Set to false if you are using PageBody inside another PageBody, in a modal or drawer
   * to avoid nested <main> elements, which are not valid HTML.
   */
  isMain?: boolean;
} & Pick<AriaAttributes, 'aria-labelledby'>;

export const PageBody = ({
  children,
  leftContent,
  rightContent,
  headerContent,
  preventScroll,
  alignIslandsToLeft,
  backgroundColor,
  backgroundPattern,
  footerContent,
  skipPadding,
  padding,
  minContentHeight,
  isMain,
  'aria-labelledby': ariaLabelledBy,
}: PageBodyProps) => (
  <FlexBox overflow="hidden" flex={1} flexDirection="column">
    <FlexBox overflow="hidden" flex={1}>
      {leftContent}
      <FlexBox
        flexDirection="column"
        flex={1}
        backgroundColor={backgroundColor}
        style={{ overflowY: 'auto' }}
      >
        {headerContent}
        <PageBodyWrapper
          as={isMain ? 'main' : 'div'}
          aria-labelledby={ariaLabelledBy}
          $preventScroll={preventScroll}
          $alignIslandsToLeft={alignIslandsToLeft}
          $backgroundColor={backgroundColor}
          $backgroundPattern={backgroundPattern}
          $skipPadding={skipPadding}
          $padding={padding}
          $minHeight={minContentHeight}
        >
          {children}
        </PageBodyWrapper>
      </FlexBox>
      {rightContent}
    </FlexBox>
    {footerContent}
  </FlexBox>
);
